"use client";
import { Track } from "@/src/lib/tracking";

import { getBridge } from "./bridge";
import { MessageType } from "./types";

/** Plaid OAuth may leave the WebView (Custom Tab / SFSafariViewController) for minutes. */
const PLAID_OPEN_LINK_TIMEOUT_MS = 5 * 60 * 1000;

export interface PlaidOpenLinkParams {
  linkToken: string;
}

/**
 * Opens Plaid Link in the native App shell. Requires the native bridge implementation.
 * Resolves when native Link completes; authorization uses Web-held link_token only.
 */
export const openPlaidLinkInApp = async (
  params: PlaidOpenLinkParams,
): Promise<void> => {
  const bridge = getBridge();
  if (!bridge.isInApp()) {
    throw new Error(
      "openPlaidLinkInApp is only available inside the App WebView",
    );
  }

  const message = await bridge.request(
    MessageType.CALL_METHOD,
    {
      method: "plaid.openLink",
      params: { linkToken: params.linkToken },
    },
    { timeoutMs: PLAID_OPEN_LINK_TIMEOUT_MS },
  );

  const response = message.data as { link_token?: string } | undefined;
  const echoedLinkToken = response?.link_token?.trim();

  if (!echoedLinkToken) {
    Track.log("error", "plaidLink", "Native openLink missing link_token", {
      cause: String(response),
    });
    throw new Error("Native Plaid Link did not return link_token");
  }

  if (echoedLinkToken !== params.linkToken) {
    Track.log("error", "plaidLink", "Native openLink link_token mismatch", {
      expected: params.linkToken,
      received: echoedLinkToken,
    });
    throw new Error("Native Plaid Link returned unexpected link_token");
  }
};
